import { useState } from 'react';

import { IconInfoCircleOutline } from '@iwoca/icons';
import { ContentSwitcher, Pill, Skeleton } from '@iwoca/orion';

import { CopyEmailLink } from './CopyEmailLink';
import { TappableTooltip } from '../../../../components/TappableTooltip/TappableTooltip';
import { displayCurrency } from '../../../../utils/DisplayCurrency';
import { daysSinceExpiry, daysUntilExpiry } from '../../expiryDays';

export interface ProfileTerm {
  availableAmount: number;
  durationInMonths: number;
  isValid: boolean;
  timeout: string | null;
}

const DURATION_ORDER = [3, 12, 1];

const durationLabel = (durationInMonths: number) =>
  durationInMonths === 1 ? '30 days' : `${durationInMonths} months`;

const orderTerms = (terms: ProfileTerm[]) =>
  [...terms].sort(
    (a, b) =>
      DURATION_ORDER.indexOf(a.durationInMonths) -
      DURATION_ORDER.indexOf(b.durationInMonths),
  );

const expiryLabel = (timeout: string) => {
  const daysLeft = daysUntilExpiry(timeout);
  if (daysLeft > 0) return `${daysLeft}d left till expiry`;

  const daysAgo = Math.max(1, daysSinceExpiry(timeout));
  return `Expired ${daysAgo} day${daysAgo > 1 ? 's' : ''} ago`;
};

const Heading = () => (
  <div className="mb-l flex items-center gap-s">
    <span className="text-xl font-bold text-primary-10">Available terms</span>
    <TappableTooltip content="Instalment plans available to this buyer">
      <IconInfoCircleOutline width={16} height={16} />
    </TappableTooltip>
  </div>
);

export const AvailableTerms = ({
  terms,
  loading,
  email,
}: {
  terms: ProfileTerm[];
  loading: boolean;
  email: string | null;
}) => {
  const orderedTerms = orderTerms(terms);
  const [selectedDuration, setSelectedDuration] = useState<number | null>(null);
  const selectedTerm =
    orderedTerms.find((term) => term.durationInMonths === selectedDuration) ??
    orderedTerms[0];

  if (loading) {
    return (
      <div>
        <Heading />
        <div className="w-full rounded-l border border-secondary-90 px-xl py-s">
          <ContentSwitcher className="my-l !h-3xl !w-full">
            <></>
          </ContentSwitcher>
          <Skeleton count={3} lastLineLength="medium" />
        </div>
      </div>
    );
  }

  if (!selectedTerm) {
    return (
      <div>
        <Heading />
        <div className="w-full rounded-l border border-secondary-90 px-xl py-2xl text-center text-m font-reg text-structure-40">
          No terms available for this buyer
        </div>
      </div>
    );
  }

  return (
    <div>
      <Heading />
      <div className="w-full rounded-l border border-secondary-90 px-xl py-s">
        <ContentSwitcher
          value={String(selectedTerm.durationInMonths)}
          className="my-l !w-full justify-between"
        >
          {orderedTerms.map((term) => (
            <ContentSwitcher.Button
              key={term.durationInMonths}
              id={String(term.durationInMonths)}
              className="w-full cursor-pointer justify-center xs:!px-s"
              onClick={() => setSelectedDuration(term.durationInMonths)}
            >
              {durationLabel(term.durationInMonths)}
            </ContentSwitcher.Button>
          ))}
        </ContentSwitcher>

        <div className="mb-s flex flex-col gap-s">
          <div className="flex items-end text-xl font-reg xs:flex-col xs:items-start xs:gap-s">
            <div>
              <b className="text-2xl font-bold">
                {displayCurrency(selectedTerm.availableAmount, 0)}
              </b>{' '}
              To spend
            </div>
            {selectedTerm.isValid ? (
              <Pill className="mb-xs ml-s h-max w-max xs:m-0" variant="green">
                Active
              </Pill>
            ) : (
              <Pill className="mb-xs ml-l h-max w-max xs:m-0" variant="london">
                Subject to approval
              </Pill>
            )}
          </div>

          <div className="grid grid-cols-[1fr_140px] items-end font-reg xs:grid-cols-1">
            <div className="text-m text-primary-10">
              {!selectedTerm.isValid &&
                "This was the buyer's last active limit. Their limit needs to be reassessed every 30 days."}
            </div>
            <div className="text-end text-s text-structure-40 xs:mt-l xs:text-start">
              {selectedTerm.timeout && expiryLabel(selectedTerm.timeout)}
            </div>
          </div>

          <CopyEmailLink email={email} />
        </div>
      </div>
    </div>
  );
};
